import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import i18n from '@/lib/i18n';
import { useOptimization } from '@/contexts/OptimizationContext';
import { useScoringJob } from '@/hooks/useScoringJob';
import { useSmoothCounter } from '@/hooks/useSmoothCounter';
import { useOptimizationEta } from '@/hooks/useOptimizationEta';
import { Phase2Texte } from './phases/Phase2Texte';
import { Phase4Score } from './phases/Phase4Score';
import { SuccessScoreboard } from './SuccessScoreboard';

// C-19: der gemessene Score-Sprung dieses Laufs — Momentum-Kontext fuer die
// Werkbank-Subline. `pre`/`post` sind einzeln nullable statt eines
// zusammengesetzten Deltas, weil je nach Lauf nur einer der beiden Werte
// vorliegen kann (kein Vorher-Score bei der ersten Optimierung; Score-Job
// noch nicht fertig) — die Werkbank entscheidet selbst, welche Fassung der
// Subline sie damit zeigt. Das Modal selbst kennt NUR dieses Delta, kein
// Feed — siehe `OptimizationSuccessContext` unten fuer den Grund.
export interface OptimizationScoreDelta {
  pre: number | null;
  post: number | null;
}

// Review-Fix Runde 1 (Feed-Scope-Leak): der globale `FeedSwitcher` im
// Layout wechselt den Feed OHNE Navigation — `location.state` einer laufenden
// Werkbank-Session bleibt dabei unangetastet stehen. Ohne eine Feed-ID im
// Kontext haette die Werkbank den Score-Sprung von Feed A weiter angezeigt,
// nachdem der Nutzer per FeedSwitcher auf Feed B umgeschaltet hat, ohne
// die Seite zu verlassen. Absichtlich NICHT hier im Modal befuellt: die
// Feed-ID, die zaehlt, ist die zum NAVIGATIONS-Zeitpunkt aktive Auswahl, frisch
// gelesen an der `navigate(...)`-Aufrufstelle (FeedHealthDashboard.tsx) — nicht
// eine Modal-Prop, die theoretisch stale sein koennte. Deshalb bleibt die
// modal-interne Weitergabe (`onReviewWorkbench`-Prop unten) auf das reine
// Score-Delta beschraenkt; `feedId` kommt erst dort dazu, wo die Werkbank ihn
// tatsaechlich braucht.
export interface OptimizationSuccessContext extends OptimizationScoreDelta {
  feedId: string;
}

interface Props {
  feedId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewWorkbench?: (ctx?: OptimizationScoreDelta) => void;
}

// Two HONEST stages. The old 4-step stepper (Sortieren→Texte→Attribute→Score)
// implied a sequence, but per batch those passes run together and across the job
// the per-phase counters advance in lockstep — so the stepper sat frozen on
// "Sortieren" ~99% of the run, then jumped to done. The only genuinely sequential
// boundary is optimize→score (scoring is a separate job that runs afterwards).
type Stage = 'optimizing' | 'scoring' | 'completed' | null;

const SCORE_JOB_WAIT_TIMEOUT_MS = 30_000;
// Heartbeat older than this → the engine is mid-Gemini-call; show a calm amber
// "thinking" pulse instead of a green "live" one so long waits never read as frozen.
const HEARTBEAT_STALE_SEC = 5;

export function OptimizationProgressModal({ feedId, open, onOpenChange, onReviewWorkbench }: Props) {
  const { t } = useTranslation('optimization');
  const job = useOptimization();

  // Track scoring as soon as optimization completes — needed to detect the
  // scoring stage before activeJob disappears from the active-jobs query.
  const scoring = useScoringJob(
    feedId,
    job.activeJob?.status === 'completed' || job.hasCompletedInSession,
  );

  // Bridge: between optimization completion and the scoring job appearing in the
  // realtime feed (~2-5s) we want to show the scoring stage, not flash Success.
  // Cap the wait so we never sit forever if the auto-trigger silently fails.
  // Once a score job HAS been seen, its disappearance from the active-jobs query
  // means it completed — go straight to Success instead of re-entering the
  // 30s wait (score jobs finish in seconds, the wait froze the modal on stale
  // counter values).
  const optimizationDone = job.hasCompletedInSession || job.activeJob?.status === 'completed';
  const [scoreJobWaiting, setScoreJobWaiting] = useState(false);
  const seenScoreJobRef = useRef(false);
  useEffect(() => {
    if (!optimizationDone) seenScoreJobRef.current = false;
  }, [optimizationDone]);
  useEffect(() => {
    if (scoring.activeScoreJob) {
      seenScoreJobRef.current = true;
      setScoreJobWaiting(false);
      return;
    }
    if (optimizationDone && !seenScoreJobRef.current) {
      setScoreJobWaiting(true);
      const tm = setTimeout(() => setScoreJobWaiting(false), SCORE_JOB_WAIT_TIMEOUT_MS);
      return () => clearTimeout(tm);
    }
    setScoreJobWaiting(false);
  }, [optimizationDone, scoring.activeScoreJob]);

  const stage: Stage = (() => {
    if (optimizationDone) {
      const sj = scoring.activeScoreJob;
      const scoringInFlight = sj && sj.status !== 'completed' && sj.status !== 'failed';
      if (scoringInFlight || scoreJobWaiting) return 'scoring';
      return 'completed';
    }
    return (job.isOptimizing || open) ? 'optimizing' : null;
  })();

  const isSuccess = stage === 'completed';
  const isScoring = stage === 'scoring';

  const displayJob = job.activeJob ?? job.completedJobSnapshot ?? null;
  const total = job.progressData?.total ?? displayJob?.total_products ?? 0;

  // Primary anchor = ONE honest monotonic number: products optimized. Er
  // wechselt in der Score-Phase bewusst NICHT auf die Zahlen des Score-Jobs:
  // der laeuft feed-weit, sein Nenner ist also eine andere Menge (10.756 statt
  // 200), und bis seine Zeile sichtbar wird, fiele der Zaehler von "200/200"
  // auf "0/200" zurueck. Der erreichte Optimierungsstand bleibt stehen; den
  // Score-Fortschritt zeigt Phase4Score mit eigener Leiste.
  const completed = job.progressData?.completed ?? displayJob?.completed_products ?? 0;
  const counterTotal = total;
  const completedSmooth = useSmoothCounter(completed);
  const progressPct = counterTotal > 0 ? Math.min(100, (completed / counterTotal) * 100) : 0;

  // 1s ticker so ETA + heartbeat liveness re-evaluate smoothly between job updates.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (isSuccess || !open) return;
    const id = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(id);
  }, [isSuccess, open]);

  // ETA + throughput (optimizing only) + heartbeat liveness.
  const aj = job.activeJob;
  // F-C (Zwergehuus-Ledger 2026-08-17): die alte Ableitung hatte drei
  // Messfehler, alle aus einer einzigen Since-Start-Rechnung `completed /
  // elapsedSec`. (1) Der Gate `elapsedSec > 8 && completed >= 10`
  // veroeffentlichte eine ETA, waehrend die Anlaufrate noch bei ~10/Min lag
  // — also viel zu NIEDRIG, wodurch die ETA um Faktor ~33 zu HOCH ausfiel
  // ("noch ~490 Min" bei tatsaechlich 14:28 min Gesamtlaufzeit). (2)
  // Seit-Start-Mittel statt Fensterrate — jede langsame Anlaufminute blieb
  // bis zum Laufende in der Rechnung. (3) keine Tail-/Stillstands-Erkennung
  // — nichts war gecacht, `etaSec` wurde bei jedem Render frisch berechnet,
  // aber ein winziger Rest gegen ein noch vom schnellen Hauptteil getragenes
  // Seit-Start-Mittel blieb minutenlang unter der 45s-Schwelle und zeigte
  // "unter 1 Min", ohne dass sich etwas bewegte (Zahlen dazu:
  // lib/optimizationEta.ts). `useOptimizationEta` ersetzt das durch
  // Anlauf-Stille, gleitendes 120s-Fenster und explizite Tail-Erkennung.
  // `ratePerMin` ist dieselbe Fensterrate und damit der ehrliche Ist-Durchsatz
  // fuer den LivePulse.
  //
  // Fix-Runde 1 (Review-Finding): `aj?.id` allein ist NICHT die stabile
  // Identitaet fuer diesen Hook. Konvergenz 4, Befund 3(c) (siehe
  // trackingUncertain unten) haelt fest, dass `job.activeJob` bei einem
  // einzelnen verschluckten Poll transient auf null faellt, waehrend der Lauf
  // weiterlaeuft — `aj?.id` durchlaeuft dann id -> null -> id ueber
  // aufeinanderfolgende Renders. `useOptimizationEta` behandelt JEDEN
  // Identitaetswechsel als neuen Job (Samples + observedSince werden
  // verworfen), ein einzelner ausgelassener Poll haette also den
  // 60s-Anlauf neu gestartet und die ETA fuer eine Minute verschwinden
  // lassen — schlechter als die alte Seit-`started_at`-Rate, die sofort
  // wieder auffrischte. `job.trackedJobId` haelt genau diese Identitaet ueber
  // die Luecke hinweg stabil (siehe useOptimizationJob.ts: gesetzt bei jedem
  // gesehenen activeJob, erst bei bestaetigtem Terminal-Status/Feed-Wechsel
  // wieder genullt) und ist deshalb der primaere Schluessel; `aj?.id` bleibt
  // nur der Fallback fuer den allerersten Render, bevor trackedJobId gesetzt
  // wird.
  const { eta, ratePerMin } = useOptimizationEta(
    job.trackedJobId ?? aj?.id ?? null,
    completed,
    counterTotal,
    stage === 'optimizing' && open,
  );

  const hbMs = aj?.heartbeat_at ? new Date(aj.heartbeat_at).getTime() : null;
  const hbStaleSec = hbMs ? (Date.now() - hbMs) / 1000 : null;
  const isThinking = !isScoring && hbStaleSec !== null && hbStaleSec > HEARTBEAT_STALE_SEC;

  // Konvergenz 4, Befund 3(c): die Query kennt den Lauf gerade nicht (ein
  // Poll kam leer zurueck — ablaufende Session, Netz), aber diese Instanz hat
  // ihn gesehen (trackedJobId) und pollt weiter. Frueher zeigte das Modal in
  // diesem Zustand einen GRUENEN "live"-Puls auf dem "Starte…"-Platzhalter —
  // ein erfundenes Signal. Jetzt: amber Verbindungs-Hinweis, bis der naechste
  // Poll die echten Zaehler zurueckbringt. localJobInfo (frischer Start, noch
  // kein erster Poll) ist bewusst KEINE Unsicherheit.
  const trackingUncertain =
    stage === 'optimizing' && !job.activeJob && !job.localJobInfo && !!job.trackedJobId;

  const etaText = (() => {
    if (isScoring) return t('progressModal.eta.almostDone');
    switch (eta.kind) {
      case 'lessThanMin': return t('progressModal.eta.lessThanMin');
      case 'finishing': return t('progressModal.eta.finishing');
      case 'minutes': return t('progressModal.eta.minutes', { count: eta.minutes });
      default: return t('progressModal.eta.calculating');
    }
  })();

  const totalSorted = job.activeJob?.sortieren_completed_products
    ?? displayJob?.sortieren_completed_products
    ?? 0;
  const categoryCountsForPanel = Object.keys(job.categoryCounts).length > 0
    ? job.categoryCounts
    : (displayJob?.sortieren_category_counts ?? {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[600px] w-full p-0 bg-card border-border max-h-[90vh] overflow-y-auto overflow-x-hidden"
        style={{
          // Override base shadcn `grid` layout — grid tracks expand to min-content
          // of children, so long unbreakable German compounds push the modal wider
          // than max-width. Block layout respects max-width strictly.
          display: 'block',
          gap: 0,
        }}
      >
        {/* Banner */}
        <div className={`px-7 py-6 relative transition-colors duration-500 ${
          isSuccess
            ? 'bg-secondary text-center'
            : 'bg-secondary'
        }`}>
          {isSuccess && (
            /* Fix-Runde 1: `0 8px 24px` hatte Versatz nach unten, hob also
               wirklich eine Flaeche vom Grund ab (kein Glow) -> shadow-md. */
            <div className="success-icon mx-auto mb-3 relative w-14 h-14 rounded-full bg-card shadow-md flex items-center justify-center">
              <div className="w-9 h-9 rounded-full bg-success flex items-center justify-center text-success-foreground text-xl font-bold">
                ✓
              </div>
            </div>
          )}
          <StageLabel stage={stage} />
          <StageHeading stage={stage} completedProducts={completed} />
        </div>

        {/* Honest 2-stage flow — the only real sequence is optimize → score → done */}
        {!isSuccess && <StageFlow stage={stage} />}

        {/* Primary anchor: ONE counter + ETA + live-pulse + bar. Hidden in success. */}
        {!isSuccess && (
          <>
            <div className="px-7 pt-3 pb-1 border-t border-border flex items-baseline gap-2">
              {/* Vorgabe 3.3: nie "0 von N" als Fortschrittsanzeige — bis zum
                  ersten fertigen Produkt steht hier eine Startaussage statt
                  einer Null in 2xl-Fettschrift. */}
              {completed > 0 ? (
                <>
                  <span className="text-2xl font-extrabold text-foreground tabular-nums">{Math.round(completedSmooth)}</span>
                  <span className="text-sm text-muted-foreground">/ {counterTotal} {t('progressModal.counter.products', { count: counterTotal })}</span>
                </>
              ) : (
                <span className="text-sm font-semibold text-foreground">{t('progressModal.counter.starting')}</span>
              )}
              <span className="ml-auto text-xs font-semibold text-muted-foreground tabular-nums">{etaText}</span>
            </div>
            <div className="px-7 pb-3">
              {/* ratePerMin ist die gleitende 120s-Fensterrate aus
                  useOptimizationEta, nicht mehr die Seit-Start-Rate — derselbe
                  ehrliche Ist-Durchsatz, den auch die ETA benutzt. */}
              <LivePulse thinking={isThinking} uncertain={trackingUncertain} throughputPerMin={ratePerMin} />
            </div>
            <div className="relative h-[3px] bg-muted overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-primary"
                style={{
                  width: `${Math.max(2, progressPct)}%`,
                  transition: 'width 0.8s cubic-bezier(0.22, 0.61, 0.36, 1)',
                }}
              />
              <div className="absolute inset-0 modal-shimmer pointer-events-none" />
            </div>
          </>
        )}

        <div className="px-7 pb-4 pt-4">
          {!isSuccess && !isScoring && (
            <div className="phase-detail-fade space-y-0">
              {/* Beide Panels laufen gleichzeitig und messen BEIDE diesen Lauf:
                  die Kategorien aus `sortieren_category_counts` der Job-Zeile,
                  der Text-Diff aus `optimization_events` dieses `job_id`.

                  Das dritte Panel (Attribute) ist ersatzlos gestrichen. Es
                  zaehlte feed-weit — alle Produkte des Feeds, bei denen je ein
                  `optimized_*`-Feld gefuellt wurde, ueber sieben Attribute
                  summiert — und beschriftete die Summe als Ertrag DIESES Laufs
                  ("Attribute ergaenzt", "+{n}"). Auf einem lange genutzten Feed
                  war das bis zu sieben Mal die Produktzahl, und weil der
                  `refetchInterval` am Layout-Flag `compact` hing, stand der
                  Wert vom Mount an fest, also von VOR dem ersten Schreibvorgang
                  des Laufs.

                  Warum nicht auf den Lauf gescopt statt gestrichen: die Zahl
                  gibt es nur als sieben `count`-Abfragen ueber die Produkte des
                  Laufs, gechunkt zu 200 gegen die PostgREST-Kappung — und die
                  Produktmenge selbst muss vorher seitenweise aus
                  `optimization_events` geholt werden (die Spalte
                  `product_ids` der Job-Zeile ist leer, siehe
                  `lib/runScore.ts`). Das sind 7 Requests je 2,5 s bei
                  einem 200er-Lauf und 378 bei 10.756 Produkten, waehrend
                  dieselbe Datenbank 20 Worker bedient. Die billige lauf-eigene
                  Groesse waere `attribute_completed_products` von der bereits
                  gepollten Job-Zeile, aber die zaehlt Produkte durch die Phase,
                  nicht ergaenzte Werte, und laeuft im Gleichschritt mit
                  `completed_products` — genau der Gleichschritt, an dem der
                  4-Schritt-Stepper gescheitert ist. Das Panel haette also die
                  Zahl wiederholt, die oben in 2xl steht.

                  Damit derselbe Vorgang wie beim feed-weiten Score-Histogramm
                  in Phase4Score: Panel weg, Begruendung bleibt hier stehen. */}
              <CategoriesPanel
                categoryCounts={categoryCountsForPanel}
                totalSorted={totalSorted}
              />
              <Phase2Texte jobId={job.activeJob?.id ?? null} />
            </div>
          )}
          {!isSuccess && isScoring && (
            <Phase4Score
              feedId={feedId}
              jobId={job.activeJob?.id ?? displayJob?.id ?? null}
              progressPct={scoring.scoreProgressPct}
            />
          )}
          {isSuccess && (
            <SuccessScoreboard
              feedId={feedId}
              job={displayJob}
              preScore={job.preScore ?? displayJob?.pre_optimization_avg_score ?? null}
              phaseTimings={job.phaseTimings}
              onReviewWorkbench={(ctx) => {
                onOpenChange(false);
                onReviewWorkbench?.(ctx);
              }}
            />
          )}
        </div>

        {/* Abbrechen bleibt auch im Unsicherheits-Fenster erreichbar
            (Befund 3c): handleCancel greift dann auf trackedJobId zurueck. */}
        {!isSuccess && (job.activeJob?.status === 'processing' || trackingUncertain) && (
          <div className="px-7 pb-3 -mt-1 text-center">
            <button
              type="button"
              onClick={() => job.handleCancel()}
              disabled={job.cancelling}
              className="text-xs text-muted-foreground hover:text-foreground/80 underline-offset-2 hover:underline disabled:opacity-40"
            >
              {job.cancelling
                ? i18n.t('dashboard:optimizationModal.cancelling')
                : i18n.t('dashboard:optimizationModal.cancelButton')}
            </button>
          </div>
        )}

        {!isSuccess && (
          <div className="text-center text-xs text-muted-foreground py-3 border-t border-border">
            {job.cleanupLock.locked
              ? i18n.t('dashboard:optimizationModal.cleaningUp', { seconds: job.cleanupLock.secondsRemaining })
              : i18n.t('dashboard:optimizationModal.runsInBackground')}
          </div>
        )}

        {/* Animation styles */}
        <style>{`
          @keyframes modal-shimmer-kf {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .modal-shimmer {
            background: linear-gradient(90deg, transparent 0%, hsl(var(--primary) / 0.18) 50%, transparent 100%);
            animation: modal-shimmer-kf 2s linear infinite;
          }

          @keyframes spin-ring {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes ring-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes phase-detail-enter {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .phase-detail-fade > * {
            animation: phase-detail-enter 0.4s cubic-bezier(0.22, 0.61, 0.36, 1);
          }

          .flow-node-wrap.is-active::before {
            content: '';
            position: absolute;
            inset: -5px;
            border-radius: 50%;
            border: 2px solid transparent;
            /* Rotierender Ladering um den aktiven Ablaufknoten, einfarbig
               auf primary (D21) statt zweifarbigem Verlauf. */
            border-top-color: hsl(var(--primary));
            animation: spin-ring 1.4s linear infinite, ring-fade-in 0.4s ease;
            pointer-events: none;
          }

          @keyframes live-pulse-kf {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.4; transform: scale(0.8); }
          }
          .live-dot { animation: live-pulse-kf 1.4s ease-in-out infinite; }

          @keyframes success-pop {
            0%   { transform: scale(0); opacity: 0; }
            70%  { transform: scale(1.15); opacity: 1; }
            100% { transform: scale(1); }
          }
          @keyframes confetti-burst {
            0%   { opacity: 0; transform: scale(0); }
            50%  { opacity: 1; }
            100% { opacity: 1; transform: scale(1); }
          }
          .success-icon { animation: success-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1); }
          .success-icon::before, .success-icon::after {
            content: '';
            position: absolute;
            width: 4px; height: 4px;
            border-radius: 50%;
            animation: confetti-burst 1s ease 0.2s both;
          }
          .success-icon::before {
            background: hsl(var(--primary));
            top: -4px; left: -8px;
            /* Konfetti-Toene aus {primary, success, warning} (D31/E18), kein violet mehr. */
            box-shadow: 70px 6px 0 hsl(var(--success)), -10px 50px 0 hsl(var(--primary)), 68px 44px 0 hsl(var(--warning));
          }
          .success-icon::after {
            background: hsl(var(--success));
            top: 14px; left: 70px;
            box-shadow: -82px 26px 0 hsl(var(--primary)), 22px -8px 0 hsl(var(--warning));
          }

          @media (prefers-reduced-motion: reduce) {
            .modal-shimmer,
            .flow-node-wrap.is-active::before,
            .live-dot { animation: none !important; }
            .phase-detail-fade > * { animation: none !important; }
            .success-icon, .success-icon::before, .success-icon::after { animation: none !important; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

function StageLabel({ stage }: { stage: Stage }) {
  const { t } = useTranslation('optimization');
  if (!stage) return null;
  const key = stage === 'completed' ? 'progressModal.phaseLabel.completed' : `progressModal.stageLabel.${stage}`;
  return (
    <div className="text-[10px] tracking-[1.5px] text-primary-ink font-bold mb-1.5 transition-opacity duration-300">
      {t(key)}
    </div>
  );
}

// `completedProducts`, nicht `total_products`: ein Job mit Teil-Fehlschlaegen
// wird 'completed' (job-lifecycle) — "200 Produkte sind optimiert" waere bei
// 12 Fehlern schlicht falsch, die richtige Zahl liegt auf derselben Job-Zeile.
function StageHeading({ stage, completedProducts }: { stage: Stage; completedProducts: number }) {
  const { t } = useTranslation('optimization');
  if (!stage) return null;
  const map: Record<Exclude<Stage, null>, { title: string; desc: string }> = {
    optimizing: { title: t('progressModal.optimizingTitle'), desc: t('progressModal.optimizingDesc') },
    scoring: { title: t('progressModal.heading.scoringTitle'), desc: t('progressModal.heading.scoringDesc') },
    completed: { title: t('progressModal.heading.completedTitle'), desc: t('progressModal.heading.completedDesc', { count: completedProducts }) },
  };
  const m = map[stage];
  // Title/Description per asChild auf die bestehende Ueberschrift: Radix zieht
  // den Fokus in dieses Modal, sobald der Nutzer "Optimieren" klickt — ohne
  // Accessible Name melden NVDA/JAWS nur "Dialog" mitten in der wichtigsten
  // Transaktion der App (WCAG 4.1.2). Solange das Modal offen ist, ist `stage`
  // nie null (siehe stage-Ableitung), der Dialog traegt also immer einen
  // Namen. Bewusst die ROHEN Radix-Primitives statt der gestylten
  // ui/dialog-Wrapper: deren Basisklassen (text-lg, leading-none, …) wuerden
  // per asChild in die h3 gemergt und die Optik verschieben — die Primitives
  // fuegen nur die aria-Verdrahtung hinzu, kein visueller Umbau.
  return (
    <>
      <DialogPrimitive.Title asChild>
        <h3 className="font-extrabold text-xl text-foreground mb-1 transition-opacity duration-300 break-words">{m.title}</h3>
      </DialogPrimitive.Title>
      <DialogPrimitive.Description asChild>
        <p className="text-sm text-muted-foreground transition-opacity duration-300 break-words">{m.desc}</p>
      </DialogPrimitive.Description>
    </>
  );
}

/** Honest 2-stage flow: Optimieren → Scoren → Fertig (current stage highlighted). */
function StageFlow({ stage }: { stage: Stage }) {
  const { t } = useTranslation('optimization');
  const nodes = ['optimize', 'score', 'done'] as const;
  const activeIdx = stage === 'optimizing' ? 0 : stage === 'scoring' ? 1 : 2;
  return (
    <div className="px-7 py-4">
      <div className="grid grid-cols-3 relative">
        <div className="absolute top-3 left-[16.67%] right-[16.67%] h-0.5 bg-muted z-0" />
        {/* Fuehrungslinie zwischen zwei Phasenpunkten, kein Statusverlauf:
            ein Farbwechsel Gruen nach Petrol auf 0,5 px Hoehe ist unter
            Protanopie ohnehin nicht lesbar (D25). */}
        <div
          className="absolute top-3 left-[16.67%] h-0.5 z-[1] bg-primary"
          style={{
            width: `${(Math.min(activeIdx, 2) / 2) * 66.66}%`,
            transition: 'width 0.9s cubic-bezier(0.22, 0.61, 0.36, 1)',
          }}
        />
        {nodes.map((n, i) => {
          const isDone = activeIdx > i;
          const isActive = activeIdx === i;
          return (
            <div key={n} className="text-center relative z-[2]">
              <div className={`flow-node-wrap relative w-6 h-6 mx-auto mb-1.5 ${isActive ? 'is-active' : ''}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold relative z-[2] border-2
                  ${isDone ? 'bg-success text-success-foreground border-success' :
                    isActive ? 'bg-primary text-primary-foreground border-primary' :
                    'bg-card border-border text-muted-foreground'}`}
                  style={{ transition: 'background 0.5s ease, border-color 0.5s ease, color 0.5s ease' }}>
                  {isDone ? '✓' : i + 1}
                </div>
              </div>
              <div className={`text-[10px] font-semibold transition-colors duration-500
                ${isDone ? 'text-success-ink' : isActive ? 'text-primary-ink font-bold' : 'text-muted-foreground'}`}>
                {t(`progressModal.flow.${n}`)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Heartbeat-driven liveness so long Gemini waits never read as "frozen".
 *  `uncertain` = die Query hat den Lauf gerade verloren (Befund 3c): amber
 *  Verbindungs-Label statt eines erfundenen gruenen "live". */
function LivePulse({ thinking, uncertain = false, throughputPerMin }: { thinking: boolean; uncertain?: boolean; throughputPerMin: number | null }) {
  const { t } = useTranslation('optimization');
  const color = (thinking || uncertain) ? 'bg-warning' : 'bg-success';
  const label = uncertain
    ? t('progressModal.live.reconnecting')
    : thinking
      ? t('progressModal.live.thinking')
      : t('progressModal.live.active');
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className={`live-dot inline-block w-2 h-2 rounded-full ${color}`} />
      <span className="font-medium">{label}</span>
      {throughputPerMin !== null && throughputPerMin > 0 && (
        <span className="text-muted-foreground">· {t('progressModal.live.throughput', { count: Math.round(throughputPerMin) })}</span>
      )}
    </div>
  );
}

function CategoriesPanel({ categoryCounts, totalSorted }: {
  categoryCounts: Record<string, number>;
  totalSorted: number;
}) {
  const { t } = useTranslation('optimization');
  const sorted = Object.entries(categoryCounts).sort(([, a], [, b]) => b - a);
  const top = sorted.slice(0, 5);
  const restSum = sorted.slice(5).reduce((s, [, n]) => s + n, 0);
  const restCats = sorted.slice(5).length;

  const CATEGORY_EMOJI: Record<string, string> = {
    fashion: '👟', home: '🏠', home_garden: '🪴', electronics: '🔌',
    food: '🍫', health_beauty: '💄', auto_parts: '🚗', tools_energy: '🔧',
    media: '📚', baby_toddler: '🍼', toys_games: '🎲', sporting: '⚽',
    general: '📦',
  };

  const prettify = (key: string): { emoji: string; label: string } => {
    const emoji = CATEGORY_EMOJI[key] ?? '📦';
    const labelKey = `progressModal.categories.labels.${key}`;
    const translated = t(labelKey);
    const label = translated === labelKey
      ? key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : translated;
    return { emoji, label };
  };

  if (sorted.length === 0) {
    return (
      <div className="bg-accent/40 border border-accent rounded-lg px-3.5 py-3 mb-3">
        <div className="text-[10px] uppercase tracking-wider text-primary-ink font-bold mb-2">
          {t('progressModal.categories.readingProducts')}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-card border border-border rounded-md h-7 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-accent/40 border border-accent rounded-lg px-3.5 py-3 mb-3">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] uppercase tracking-wider text-primary-ink font-bold">
          {t('progressModal.categories.headerActive')}
        </span>
        <span className="text-[10px] text-muted-foreground font-mono">
          {t('progressModal.categories.sorted', { count: totalSorted })}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {top.map(([key, n]) => {
          const d = prettify(key);
          return (
            <div
              key={key}
              className="bg-card border border-border rounded-md px-2 py-1.5 flex items-center gap-1 text-[10px]"
            >
              <span className="text-xs">{d.emoji}</span>
              <span className="text-muted-foreground flex-1 truncate">{d.label}</span>
              <span className="font-extrabold text-foreground font-mono text-[11px]">{n}</span>
            </div>
          );
        })}
        {restCats > 0 && (
          <div className="bg-card border border-border rounded-md px-2 py-1.5 flex items-center gap-1 text-[10px] opacity-60">
            <span className="text-xs">+{restCats}</span>
            <span className="text-muted-foreground flex-1 truncate">{t('progressModal.categories.more')}</span>
            <span className="font-extrabold text-foreground font-mono text-[11px]">{restSum}</span>
          </div>
        )}
      </div>
    </div>
  );
}
